import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import LanguageThemeControls from "@/components/LanguageThemeControls";
import { APP_SHORT_NAME } from "@/constants/branding";

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.companies"), path: "/companies" },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backdropFilter: "blur(12px)",
          backgroundColor: (th) =>
            th.palette.mode === "dark" ? "rgba(10,10,10,0.8)" : "rgba(255,255,255,0.9)",
          borderBottom: 1,
          borderColor: "divider",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <BrandMark size={36} fontSize={14} borderRadius={2} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {APP_SHORT_NAME}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                mr: 2,
              }}
            >
              {navItems.map((item) => (
                <Typography
                  key={item.path}
                  variant="body2"
                  onClick={() => navigate(item.path)}
                  sx={{
                    fontWeight: 500,
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <LanguageThemeControls variant="toolbar" />
            {isAuthenticated ? (
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate("/account")}
              >
                {t("nav.account")}
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={() => navigate("/login")}>
                {t("auth.signIn")}
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ color: "text.primary" }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <List sx={{ width: 240, pt: 2 }}>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Toolbar />
    </>
  );
}
